import type {
	ComponentProps,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
} from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type TableRowType = 'body' | 'header';

const DefaultTableRowType: TableRowType = 'body' as const;

export type TableRowState =
	| 'enabled'
	| 'selected'
	| 'disabled'
	| 'warning'
	| 'success'
	| 'error';

const DefaultTableRowState: TableRowState = 'enabled' as const;

/**
 *	Inside a `Table` the row takes the table's size, so this is for a row with
 *	no table around it. Left out, the cells keep their default height.
 */
export type TableRowSize = Extract<Size, 'sm' | 'md'>;

type TableRowEventHandlers = {
	[K in keyof ComponentProps<'tr'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'tr'>[K];
};

interface TableRowDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TableRowProperties
	extends TableRowEventHandlers, TableRowDataAttributes {
	type?: TableRowType;
	state?: TableRowState;
	size?: TableRowSize;

	/** The row's cells. A row is the one atom that takes children. */
	children: ReactNode;
}

/**
 *	A control inside a cell owns its own click. Without this, pressing a cell's
 *	menu or checkbox would also open the row it sits in.
 */
const InteractiveDescendant =
	'a, button, input, select, textarea, label, [role="button"]';

function startedOnControl(
	event: MouseEvent<HTMLTableRowElement> | KeyboardEvent<HTMLTableRowElement>
): boolean {
	const control = (event.target as HTMLElement).closest(InteractiveDescendant);

	return control !== null && event.currentTarget.contains(control);
}

/**
 *	One record of a table. The row carries the state, so the whole line changes
 *	colour together: its cells are see-through and read their divider and text
 *	colour off the custom properties the row declares.
 */
export function TableRow({
	type = DefaultTableRowType,
	state = DefaultTableRowState,
	size,

	children,

	onClick,
	onKeyDown,
	...props
}: TableRowProperties): ReactNode {
	// A disabled record cannot be acted on, so it drops its click as well.
	const clickable: boolean = onClick !== undefined && state !== 'disabled';

	const className: string = cn(
		css.row,
		css[type],
		css[state],
		size && css[size],
		clickable && css.clickable
	);

	const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
		if (!startedOnControl(event)) {
			onClick?.(event);
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
		onKeyDown?.(event);

		const activates = event.key === 'Enter' || event.key === ' ';

		if (activates && event.target === event.currentTarget) {
			// Space would otherwise scroll the page instead of opening the row.
			event.preventDefault();
			event.currentTarget.click();
		}
	};

	return (
		<tr
			{...props}
			data-type={type}
			data-state={state}
			className={className}
			onClick={clickable ? handleClick : undefined}
			onKeyDown={clickable ? handleKeyDown : onKeyDown}
			tabIndex={clickable ? 0 : undefined}>
			{children}
		</tr>
	);
}
